import React, { useState } from "react";
import { Box, TextField, Button, Alert } from "@mui/material";
import styled from "styled-components";

const ContactWrapper = styled.section`
  width: 100%;
  padding: 60px 0;
  position: relative;
  z-index: 2;
`;

const ContactContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    max-width: 90%;
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const GlassCard = styled.div`
  background: #f0f0f0;
  backdrop-filter: blur(10px);
  border-radius: 30px;

  padding: 40px;
  position: relative;
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: 30px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: #335da9;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const InfoLabel = styled.span`
  color: #1976d2;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const InfoText = styled.span`
  color: #333;
  font-size: 1.1rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 20px;
  width: 100%;

  .MuiOutlinedInput-root {
    height: ${(props) => (props.multiline ? "auto" : "55px")};
    background: rgba(255, 255, 255, 0.8);
    border-radius: 15px;
    padding: 15px;

    @media (max-width: 768px) {
      height: ${(props) => (props.multiline ? "auto" : "50px")};
      padding: 12px;
    }

    fieldset {
      border: 1px solid rgba(33, 150, 243, 0.2);
    }

    input,
    textarea {
      color: #333;
      font-size: 1rem;

      @media (max-width: 768px) {
        font-size: 0.9rem;
      }

      &::placeholder {
        color: rgba(0, 0, 0, 0.5);
      }
    }
  }

  .MuiInputLabel-root {
    color: #666;
    font-size: 1rem;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const SubmitButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    border: none;
    box-shadow: 0 4px 15px rgba(33, 150, 243, 0.2);
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 12px 30px;
      font-size: 0.9rem;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 80px;
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    height: 150px;
    margin-bottom: 20px;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
  });

  const [alert, setAlert] = useState({
    show: false,
    type: "info",
    message: "",
  });

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const re = /^(\+91|0)?[6-9]\d{9}$/;
    return re.test(phone);
  };

  const validateForm = () => {
    const newErrors = {
      fullName: "",
      email: "",
      phoneNumber: "",
      message: "",
    };
    let isValid = true;

    // Validate Full Name
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Name must be at least 3 characters long";
      isValid = false;
    }

    // Validate Email
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate Phone Number
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
      isValid = false;
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid Indian phone number";
      isValid = false;
    }

    // Validate Message
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
      isValid = false;
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phoneNumber: "",
      message: "",
    });
    setErrors({
      fullName: "",
      email: "",
      phoneNumber: "",
      message: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await fetch(
        "https://dropshipindia.live/dropship/support/query",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.fullName,
            email: formData.email,
            phone_number: formData.phoneNumber,
            message: formData.message,
            source: "dropship-india",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit support query");
      }

      setAlert({
        show: true,
        type: "success",
        message: data.message || "Message sent successfully!",
      });
      resetForm();
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        message: error.message || "Failed to send message. Please try again.",
      });
    }

    setTimeout(() => {
      setAlert({ show: false, type: "info", message: "" });
    }, 5000);
  };

  return (
    <ContactWrapper>
      <ContactContainer>
        <GlassCard>
          <ImageWrapper>
            <img src="/images/contact images/contact-us.png" alt="Contact Us" />
          </ImageWrapper>
          <Title>Contact Us</Title>
          <ContactInfo>
            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoText>support@dropshipindia.live</InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Instagram</InfoLabel>
              <InfoText>@dropshipindia.live</InfoText>
            </InfoItem>
          </ContactInfo>
        </GlassCard>

        <GlassCard>
          <ImageWrapper>
            <img
              src="/images/contact images/contact-form.png"
              alt="Send Message"
            />
          </ImageWrapper>
          <Title>Send Us A Message</Title>
          {alert.show && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity={alert.type}
                onClose={() =>
                  setAlert({ show: false, type: "info", message: "" })
                }
              >
                {alert.message}
              </Alert>
            </Box>
          )}
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <StyledTextField
                name="fullName"
                label="Full Name"
                variant="outlined"
                value={formData.fullName}
                onChange={handleChange}
                error={!!errors.fullName}
                helperText={errors.fullName}
                required
              />
              <StyledTextField
                name="email"
                label="Email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Box>
            <StyledTextField
              name="phoneNumber"
              label="Phone Number"
              variant="outlined"
              value={formData.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              required
              fullWidth
              sx={{ mb: 3 }}
            />
            <StyledTextField
              name="message"
              label="Message"
              variant="outlined"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              error={!!errors.message}
              helperText={errors.message}
              required
              fullWidth
              sx={{ mb: 3 }}
            />
            <SubmitButton type="submit" variant="contained" fullWidth>
              Send Message
            </SubmitButton>
          </form>
        </GlassCard>
      </ContactContainer>
    </ContactWrapper>
  );
};

export default Contact;
